/**
 * 11to1 Product App API contract.
 *
 * This file intentionally documents only the live frontend surface.
 */

export const PREFERENCES_ENDPOINT = '/me/preferences';

export const CONTROL_STATUS_ENDPOINT = '/control/status';
export const CONTROL_ONBOARDING_ENDPOINT = '/control/onboarding';

export const AUTH_LOGIN_ENDPOINT = '/auth/login';
export const AUTH_ME_ENDPOINT = '/auth/me';
export const SETUP_PROGRESS_ENDPOINT = '/setup-progress';
export const SETTINGS_KEYS_ENDPOINT = '/settings/keys';

export function settingsKeyEndpoint(provider: string): string {
  return `${SETTINGS_KEYS_ENDPOINT}/${provider}`;
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Scout v0 — criteria composer (P14/P15 backend, P16 frontend)            */
/* All routes are under /scout/* and gated by the 'scout' entitlement.      */
/* ─────────────────────────────────────────────────────────────────────── */

export const SCOUT_CRITERIA_PROFILE_ENDPOINT = '/scout/criteria/profile';
export const SCOUT_TEMPLATES_ENDPOINT = '/scout/criteria/templates';

export function scoutTemplateDetailEndpoint(templateId: string): string {
  return `${SCOUT_TEMPLATES_ENDPOINT}/${templateId}`;
}

export function scoutTemplateArchiveEndpoint(templateId: string): string {
  return `${SCOUT_TEMPLATES_ENDPOINT}/${templateId}/archive`;
}

export const SCOUT_COMPOSE_ENDPOINT = '/scout/compose';
export const SCOUT_COMPOSE_PROPOSE_ENDPOINT = '/scout/compose/propose';
export const SCOUT_COMPOSE_CONFIRM_ENDPOINT = '/scout/compose/confirm';
export const SCOUT_COMPOSE_PREVIEW_ENDPOINT = '/scout/compose/preview';
export const SCOUT_COMPOSE_LAUNCH_ENDPOINT = '/scout/compose/launch';

/* Run status (P17) — run history list + per-run detail. */
export const SCOUT_RUNS_ENDPOINT = '/scout/runs';

export function scoutRunDetailEndpoint(runId: string): string {
  return `${SCOUT_RUNS_ENDPOINT}/${runId}`;
}

/* Resume a FAILED run from its checkpoints (P26). Wraps the P07 resume lane
   (same as `pnpm scout:resume`) — one lane, two interfaces. */
export function scoutRunResumeEndpoint(runId: string): string {
  return `${SCOUT_RUNS_ENDPOINT}/${runId}/resume`;
}

/* Human QA gate (P18) — per-run queue, decision submit, session summary.
   One lane, two interfaces: the decision submit writes through the same
   feedback records the CLI path (P04) uses. */
export function scoutQaQueueEndpoint(runId: string): string {
  return `/scout/qa/${runId}/queue`;
}

export function scoutQaDecisionEndpoint(runId: string): string {
  return `/scout/qa/${runId}/decision`;
}

export function scoutQaSummaryEndpoint(runId: string): string {
  return `/scout/qa/${runId}/summary`;
}

/* Export screen (P19) — preview, generate, download, history.
   One lane, two interfaces: the export UI wraps the same P03 export lane
   (assembleExportEntities + renderExport) the CLI uses. Write-back semantics
   preserved: export does NOT re-write touch_history clocks (the QA lane already
   did); re-export is idempotent (identical CSV + upserted history row). */
export type ExportScope = 'ab' | 'cd';

/** The three always-on suppression list types (§1.3 layer 1). */
export type SuppressionListType = 'clients' | 'competitors' | 'badFit';

export function scoutExportPreviewEndpoint(runId: string, scope: ExportScope = 'ab'): string {
  return `/scout/export/${runId}/preview?scope=${scope}`;
}

export function scoutExportGenerateEndpoint(runId: string, scope: ExportScope = 'ab'): string {
  return `/scout/export/${runId}/generate?scope=${scope}`;
}

/** The download endpoint — returns text/csv (NOT JSON), so it is fetched
 *  directly via window.open / anchor, NOT through apiV2Fetch (which parses
 *  JSON). The CSRF preflight is not required for GET downloads. */
export function scoutExportDownloadEndpoint(runId: string, scope: ExportScope = 'ab'): string {
  return `/scout/export/${runId}/download?scope=${scope}`;
}

export function scoutExportHistoryEndpoint(runId: string): string {
  return `/scout/export/${runId}/history`;
}

/* Workspace always-on suppression lists (P20) — §1.3 layer 1.
   Bulk paste domains/emails into typed lists (clients / competitors /
   bad-fit). Competitor entries are review-first (locked nuance: recruiter ≠
   end-employer). Same suppressions JSONB the P14 profile route owns; entries
   take effect at compose time (P15 merge). Never shared cross-workspace. */
export const SCOUT_SUPPRESSIONS_ENDPOINT = '/scout/suppressions';

export function scoutSuppressionTypeEndpoint(type: SuppressionListType): string {
  return `${SCOUT_SUPPRESSIONS_ENDPOINT}/${type}`;
}

export const SCOUT_SUPPRESSIONS_ADD_ENDPOINT = '/scout/suppressions/add';
export const SCOUT_SUPPRESSIONS_REMOVE_ENDPOINT = '/scout/suppressions/remove';

/* §1.6 human-approval gate — promote a QA-created 'pending_review' rule (held
   by the P06 volume guard) to 'active'. Targets scout_suppressions ROWS (the
   QA-feedback tier), distinct from the always-on JSONB lists above. Backend:
   404 cross-org, 409 when the rule is not pending_review. */
export function scoutSuppressionPromoteEndpoint(suppressionId: string): string {
  return `${SCOUT_SUPPRESSIONS_ENDPOINT}/${suppressionId}/promote`;
}
